import React, { memo, useMemo } from 'react';
import {
    LineChart,
    BarChart,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Line,
    Bar,
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    AreaChart,
    Area,
    ScatterChart,
    Scatter,
    RadialBarChart,
    RadialBar,
    ComposedChart,
    LabelList,
} from 'recharts';

import { RechartsConfig } from '@/shared/types/chart';
import { useTranslation } from 'react-i18next';

interface RechartsRendererProps {
    config: RechartsConfig;
    title?: string;
    className?: string;
}

interface YAxisConfig {
    dataKey: string;
    displayName: string;
    orientation?: 'left' | 'right';
    type?: 'line' | 'bar' | 'area';
}

type RawYAxisItem = string | {
    dataKey?: string;
    name?: string;
    orientation?: string;
    type?: string;
};

// Paleta de cores vibrantes
const colors = [
    '#2563eb',  // Azul vibrante
    '#dc2626',  // Vermelho
    '#16a34a',  // Verde
    '#f59e0b',  // Laranja
    '#8b5cf6',  // Roxo
    '#ec4899',  // Rosa
    '#06b6d4',  // Ciano
    '#84cc16',  // Lima
    '#f97316',  // Laranja escuro
    '#6366f1',  // Indigo
    '#14b8a6',  // Teal
    '#a855f7',  // Purpura
];

// Funcao para converter strings como "1.7MM" ou "1.7K" para numeros
const parseFormattedNumber = (value: string): number => {
    if (typeof value !== 'string') return Number(value) || 0;

    if (/^-?\d+(\.\d+)?$/.test(value)) {
        return parseFloat(value);
    }

    const multipliers: { [key: string]: number } = {
        'K': 1000,
        'M': 1000000,
        'MM': 1000000,
        'B': 1000000000,
    };

    let sanitizedValue = value.replace(/R\$\s?/, '').trim();

    if (sanitizedValue.includes(',')) {
        sanitizedValue = sanitizedValue.replace(/\./g, '').replace(',', '.');
    } else if ((sanitizedValue.match(/\./g) || []).length > 1) {
        sanitizedValue = sanitizedValue.replace(/\./g, '');
    }

    const match = sanitizedValue.match(/^([\d\.-]+)([KMB]+)?$/i);

    if (match) {
        const number = parseFloat(match[1]);
        const unit = match[2]?.toUpperCase();
        return number * (multipliers[unit] || 1);
    }

    return parseFloat(sanitizedValue) || 0;
};

// Funcao para converter todo o conjunto de dados
const convertDataToNumbers = (
    data: { [key: string]: string | number }[],
    yAxis: string[],
) => {
    return data.map((row) => {
        const newRow = { ...row };
        yAxis.forEach((key) => {
            if (typeof row[key] === 'string') {
                newRow[key] = parseFormattedNumber(row[key] as string);
            }
        });
        return newRow;
    });
};

// Formata numeros no formato brasileiro
const formatNumber = (value: number) => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    if (absValue >= 1000000000) {
        const formatted = (absValue / 1000000000).toFixed(1).replace('.', ',');
        return `${sign}${formatted} Bi`;
    }
    if (absValue >= 1000000) {
        const formatted = (absValue / 1000000).toFixed(1).replace('.', ',');
        return `${sign}${formatted} Mi`;
    }
    if (absValue >= 1000) {
        const formatted = (absValue / 1000).toFixed(1).replace('.', ',');
        return `${sign}${formatted} K`;
    }
    return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
};

export function RechartsRenderer({ config, title, className = "" }: RechartsRendererProps) {

    const { t } = useTranslation("shared");

    if (!config || !config.data || !Array.isArray(config.data) || config.data.length === 0) {
        return (
            <div className={`p-4 border border-gray-200 rounded-lg bg-gray-50 ${className}`}>
                <p className="text-gray-500 text-center">{t("charts.noData")}</p>
            </div>
        );
    }

    const { chartType, data, xAxis, yAxis } = config;

    const yAxisConfigs = useMemo((): YAxisConfig[] => {
        if (!yAxis) return [];
    
        const rawArray = (Array.isArray(yAxis) ? yAxis : [yAxis]) as RawYAxisItem[];
        
        return rawArray.map((item): YAxisConfig => {
            // Fallback de segurança (usamos dicionário para receber dataKey e displayName diferentes)
            if (typeof item === 'string') {
                return { 
                    dataKey: item,
                    displayName: item,
                    orientation: 'left' as const,
                    type: 'line' as const
                };
            }
            
            const key = item.dataKey || item.name || '';
            return {
                dataKey: String(key),
                displayName: String(item.name || item.dataKey || ''),
                orientation: (item.orientation === 'right' ? 'right' : 'left') as 'left' | 'right',
                type: (item.type === 'bar' || item.type === 'area' ? item.type : 'line') as 'line' | 'bar' | 'area'
            };
        }).filter(c => c.dataKey !== '');
    }, [yAxis]);

    const yAxisArray = useMemo(() => yAxisConfigs.map(c => c.dataKey), [yAxisConfigs]);

    if (yAxisArray.length === 0) {
        console.error('[RechartsRenderer] Nao foi possivel determinar eixos Y a partir de:', yAxis);
        return (
            <div className={`p-4 border border-yellow-200 rounded-lg bg-yellow-50 ${className}`}>
                <p className="text-yellow-700 text-center">{t("charts.invalidYAxis")}</p>
            </div>
        );
    }

    const convertedData = useMemo(() => {
        // Para BarChart horizontal, não converter as categorias (yAxis) para número
        const isHorizontalBar = chartType === 'BarChart' &&
            yAxisArray.length > 0 &&
            data.length > 0 &&
            data.every(item => {
                const yValue = item[yAxisArray[0]];
                return typeof yValue === 'string';
            });

        if (isHorizontalBar) {
            // Converter apenas os valores (xAxis), não as categorias (yAxis)
            const result = data.map((row) => {
                const newRow = { ...row };
                if (typeof row[xAxis] === 'string') {
                    newRow[xAxis] = parseFormattedNumber(row[xAxis] as string);
                }
                return newRow;
            });
            return result;
        }

        // Comportamento padrão: converter yAxis
        const result = convertDataToNumbers(data, yAxisArray);
        return result;
    }, [data, yAxisArray, chartType, xAxis]);

    const renderLines = () =>
        yAxisConfigs.map((cfg, index) => (
            <Line
                key={cfg.dataKey}
                type="monotone"
                dataKey={cfg.dataKey}
                name={cfg.displayName}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                yAxisId={cfg.orientation || 'left'}
            />
        ));

    const renderBars = () =>
        yAxisConfigs.map((cfg, index) => (
            <Bar 
                key={cfg.dataKey} 
                dataKey={cfg.dataKey} 
                name={cfg.displayName}
                fill={colors[index % colors.length]}
                yAxisId={cfg.orientation || 'left'}
            >
                <LabelList dataKey={cfg.dataKey} position="inside" formatter={formatNumber} />
            </Bar>
        ));

    const renderStackedBars = () =>
        yAxisConfigs.map((cfg, index) => (
            <Bar
                key={cfg.dataKey}
                dataKey={cfg.dataKey}
                fill={colors[index % colors.length]}
                stackId="a"
                name={cfg.displayName}
                yAxisId={cfg.orientation || 'left'}
            >
                <LabelList
                    dataKey={cfg.dataKey}
                    position="inside"
                    fill="#ffffff"
                    formatter={formatNumber}
                />
            </Bar>
        ));

    const renderAreas = () =>
        yAxisConfigs.map((cfg, index) => (
            <Area
                key={cfg.dataKey}
                dataKey={cfg.dataKey}
                fill={colors[index % colors.length]}
                stroke={colors[index % colors.length]}
                name={cfg.displayName}
                yAxisId={cfg.orientation || 'left'}
                fillOpacity={0.4}
            />
        ));

    const renderPies = () => {
        const firstCfg = yAxisConfigs[0];
        if (!firstCfg) return null;
        return (
            <Pie
                data={convertedData}
                dataKey={firstCfg.dataKey}
                nameKey={xAxis}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill={colors[0]}
                label={(entry) => `${entry.name}: ${formatNumber(entry.value)}`}
            >
                {convertedData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
            </Pie>
        );
    };

    const renderRadars = () =>
        yAxisConfigs.map((cfg, index) => (
            <Radar
                key={cfg.dataKey}
                dataKey={cfg.dataKey}
                stroke={colors[index % colors.length]}
                fill={colors[index % colors.length]}
                fillOpacity={0.6}
                name={cfg.displayName}
            />
        ));

    const renderRadialBars = () =>
        yAxisConfigs.map((cfg, index) => (
            <RadialBar
                key={cfg.dataKey}
                dataKey={cfg.dataKey}
                fill={colors[index % colors.length]}
                cornerRadius={5}
                name={cfg.displayName}
            />
        ));

    const renderScatter = () =>
        yAxisConfigs.map((cfg, index) => (
            <Scatter
                key={cfg.dataKey}
                data={data}
                fill={colors[index % colors.length]}
                dataKey={cfg.dataKey}
                name={cfg.displayName}
            />
        ));

    const renderComposedChart = () => {
        const hasRightAxis = yAxisConfigs.some(c => c.orientation === 'right');

        return (
            <ComposedChart data={convertedData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={xAxis} angle={45} textAnchor="start" />
                
                {/* Eixo Y Principal (Esquerdo) */}
                <YAxis 
                    yAxisId="left" 
                    orientation="left" 
                    tickFormatter={formatNumber} 
                />

                {/* Eixo Y Secundário (Direito) - Só renderiza se houver dados para ele */}
                {hasRightAxis && (
                    <YAxis 
                        yAxisId="right" 
                        orientation="right" 
                        tickFormatter={formatNumber} 
                    />
                )}

                <Tooltip formatter={(val) => formatNumber(Number(val))} />
                <Legend layout="horizontal" verticalAlign="top" />

                {yAxisConfigs.map((cfg, idx) => {
                    const chartProps = {
                        dataKey: cfg.dataKey,
                        yAxisId: cfg.orientation || 'left',
                        name: cfg.displayName,
                        stroke: colors[idx % colors.length],
                        fill: colors[idx % colors.length],
                    };

                    if (cfg.type === 'bar') {
                        return <Bar key={cfg.dataKey} {...chartProps} barSize={20} />;
                    }
                    if (cfg.type === 'area') {
                        return <Area key={cfg.dataKey} {...chartProps} fillOpacity={0.3} />;
                    }
                    return (
                        <Line 
                            key={cfg.dataKey} 
                            {...chartProps} 
                            type="monotone" 
                            strokeWidth={2} 
                            dot={{ r: 4 }} 
                        />
                    );
                })}
            </ComposedChart>
        );
    };

    const renderChart = () => {
        switch (chartType) {
            case 'LineChart':
                return (
                    <LineChart data={convertedData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xAxis} angle={45} textAnchor="start" />
                        <YAxis yAxisId="left" tickFormatter={formatNumber} />
                        <Tooltip formatter={(value: number) => formatNumber(value as number)} />
                        <Legend layout="horizontal" verticalAlign="top" align="center" />
                        {renderLines()}
                    </LineChart>
                );
            case 'BarChart':
            case 'StackedBarChart':
                // Detectar se é um gráfico horizontal: yAxis contém categorias (strings) e xAxis contém números
                // Quando a config tem yAxis=["conta"] e xAxis="analise_vertical", é horizontal
                const xAxisKey = String(xAxis);
                const isHorizontal = yAxisArray.length > 0 &&
                    convertedData.length > 0 &&
                    convertedData.every(item => {
                        const xValue = item[xAxisKey];
                        const yValue = item[yAxisArray[0]];
                        return typeof xValue === 'number' && typeof yValue === 'string';
                    });

                if (convertedData.length > 0) {
                    const sample = convertedData[0];
                }

                if (isHorizontal) {
                    // BarChart Horizontal: valores no eixo X (xAxisKey), categorias no eixo Y (yAxisArray[0])
                    return (
                        <BarChart
                            data={convertedData}
                            margin={{ top: 20, right: 80, left: 80, bottom: 20 }}
                            layout="vertical"
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tickFormatter={formatNumber} />
                            <YAxis dataKey={yAxisArray[0]} type="category" width={75} />
                            <Tooltip formatter={(value: number) => formatNumber(value as number)} />
                            <Legend />
                            <Bar
                                dataKey={xAxisKey}
                                fill={colors[0]}
                                name={xAxisKey}
                            >
                                <LabelList
                                    dataKey={xAxisKey}
                                    position="insideRight"
                                    fill="#ffffff"
                                    formatter={formatNumber}
                                    offset={5}
                                />
                            </Bar>
                        </BarChart>
                    );
                }

                // BarChart Vertical (padrão)
                return (
                    <BarChart data={convertedData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xAxis} angle={45} textAnchor="start" />
                        <YAxis yAxisId="left" tickFormatter={formatNumber} />
                        <Tooltip formatter={(value: number) => formatNumber(value as number)} />
                        <Legend layout="horizontal" verticalAlign="top" align="center" />
                        {chartType === 'StackedBarChart' ? renderStackedBars() : renderBars()}
                    </BarChart>
                );
            case 'PieChart':
                return (
                    <PieChart>
                        {renderPies()}
                        <Tooltip formatter={(value: number) => formatNumber(value as number)} />
                        <Legend />
                    </PieChart>
                );
            case 'AreaChart':
                return (
                    <AreaChart data={convertedData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xAxis} angle={45} textAnchor="start" />
                        <YAxis yAxisId="left" tickFormatter={formatNumber} />
                        <Tooltip formatter={(value: number) => formatNumber(value as number)} />
                        <Legend layout="horizontal" verticalAlign="top" align="center" />
                        {renderAreas()}
                    </AreaChart>
                );
            case 'RadarChart':
                return (
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={convertedData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey={xAxis} />
                        <PolarRadiusAxis />
                        <Tooltip formatter={(value: number) => formatNumber(value as number)} />
                        <Legend />
                        {renderRadars()}
                    </RadarChart>
                );
            case 'ScatterChart':
                return (
                    <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <CartesianGrid />
                        <XAxis type="category" dataKey={xAxis} name={xAxis} />
                        <YAxis type="number" dataKey={yAxisArray[0]} name={yAxisArray[0]} unit="" />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                        <Legend />
                        {renderScatter()}
                    </ScatterChart>
                );
            case 'RadialBarChart':
                return (
                    <RadialBarChart
                        cx="50%"
                        cy="50%"
                        innerRadius="20%"
                        outerRadius="80%"
                        barSize={10}
                        data={convertedData}
                    >
                        {renderRadialBars()}
                        <Legend iconSize={10} width={120} height={140} layout="vertical" verticalAlign="middle" align="right" />
                        <Tooltip />
                    </RadialBarChart>
                );
            case 'ComposedChart':
                return renderComposedChart();
            default:
                return <p>{t("charts.unknownChartType", { type: chartType })}</p>;
        }
    };

    return (
        <div className={`p-4 border border-gray-200 rounded-lg bg-white ${className}`}>
            {title && (
                <h3 className="text-lg font-semibold mb-4 text-center text-gray-800">
                    {title}
                </h3>
            )}
            <div className="w-full">
                <ResponsiveContainer width="100%" height={500}>
                    {renderChart()}
                </ResponsiveContainer>
            </div>
            <div className="mt-2 text-xs text-gray-500 text-center">
                Tipo: {chartType} | Dados: {data.length} ponto(s)
            </div>
        </div>
    );
}

// Memoize RechartsRenderer to prevent expensive chart re-renders
export const RechartsRendererMemo = memo(RechartsRenderer);
